import { Menu } from '@headlessui/react'
import { BlandWebClient } from 'bland-client-js-sdk'
import Button from 'components/core/Button'
import BlockInterruptions from 'components/Dashboard/Parameters/BlockInterruptions'
import Language from 'components/Dashboard/Parameters/Language'
import Pronunciation from 'components/Dashboard/Parameters/Pronunciation'
import RequestData from 'components/Dashboard/Parameters/RequestData'
import Voice from 'components/Dashboard/Parameters/Voice'
import config from 'config'
import { PhoneOff, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { FaCaretDown, FaEdit, FaTrash } from 'react-icons/fa'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import { getAuthToken } from 'utils/funcs/browser/getAuthToken'

function WebClientSlideOut({
  isOpen,
  onClose,
  pathwayId,
  version,
  setCallID,
  setShowChat,
  setShowChatInterface,
}) {
  const [isCallActive, setIsCallActive] = useState(false)
  const [blandClient, setBlandClient] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [savedConfigs, setSavedConfigs] = useState([])
  const [configName, setConfigName] = useState('')
  const [selectedConfig, setSelectedConfig] = useState(null)
  const [editingConfig, setEditingConfig] = useState(null)
  const dropdownRef = useRef(null)

  const token = getAuthToken()

  const methods = useForm({
    defaultValues: {
      voice: 'june',
      language: 'en',
      request_data: null,
      block_interruptions: false,
      pronunciation_guide: null,
    },
  })

  // Define loadSavedConfigs before its use in useEffect
  const loadSavedConfigs = async () => {
    try {
      const response = await $fetch('/call_config/read', {
        headers: {
          Authorization: token,
        },
      })
      if (response.errors)
        throw new Error('Failed to fetch configurations')
      const configs = response.data
      setSavedConfigs([...configs])
    }
    catch {
      toast.error('Failed to load saved configurations')
    }
  }

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (blandClient) {
        blandClient.stopConversation()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (blandClient) {
        blandClient.stopConversation()
      }
    }
  }, [blandClient])

  useEffect(() => {
    if (isOpen) {
      loadSavedConfigs()
    }
  }, [isOpen])

  const saveConfig = async () => {
    if (!configName) {
      toast.error('Please enter a name for your configuration')
      return
    }
    try {
      const configData = {
        name: configName,
        call_config: methods.getValues(),
      }

      let response
      if (editingConfig) {
        response = await $fetch('/call_config', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token,
          },
          body: JSON.stringify({ ...configData, id: editingConfig.id }),
        })
      }
      else {
        response = await $fetch('/call_config', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token,
          },
          body: JSON.stringify(configData),
        })
      }

      if (response.errors)
        throw new Error('Failed to save configuration')
      const savedConfig = response.data

      setSavedConfigs((prev) => {
        const index = prev.findIndex(c => c.id === savedConfig.id)
        if (index !== -1) {
          return prev.map(c => (c.id === savedConfig.id ? savedConfig : c))
        }
        else {
          return [...prev, savedConfig]
        }
      })
      setSelectedConfig(savedConfig)
      setEditingConfig(savedConfig)
      toast.success(
        `Configuration ${editingConfig ? 'updated' : 'created'} successfully`,
      )
    }
    catch {
      toast.error('Failed to save configuration')
    }
  }

  const loadConfig = (config) => {
    methods.reset(config.call_config)
    setSelectedConfig(config)
  }

  const deleteConfig = async (configId) => {
    try {
      const response = await $fetch(`/call_config`, {
        method: 'DELETE',
        headers: {
          Authorization: token,
        },
        body: JSON.stringify({ id: configId }),
      })

      if (response.errors) {
        throw new Error('Failed to delete config')
      }
      setSavedConfigs(prev =>
        prev.filter(config => config.id !== configId),
      )
      if (selectedConfig?.id === configId) {
        setSelectedConfig(null)
        setEditingConfig(null)
      }
      toast.success('Configuration deleted successfully')
    }
    catch {
      toast.error('Failed to delete configuration')
    }
  }

  const startEditing = (config) => {
    setConfigName(config.name)
    setEditingConfig(config)
    methods.reset(config.call_config)
  }

  const handleDeleteConfig = (config) => {
    // Using this approach instead of window.confirm to avoid linter errors
    // In a real app, you might want to use a modal dialog component
    if (config) {
      deleteConfig(config.id)
      toast.info(`Deleted configuration: ${config.name}`)
    }
  }

  const handleStartCall = async (formData) => {
    setIsLoading(true)
    try {
      const body = {
        pathway_id: pathwayId,
        pathway_version: version.version_number,
        voice: formData.voice,
        language: formData.language || 'en',
        request_data: formData.request_data?.reduce((acc, item) => {
          acc[item.key] = item.value
          return acc
        }, {}),
        block_interruptions: formData.block_interruptions,
        pronunciation_guide: formData.pronunciation_guide,
      }

      const headers = {
        'Content-Type': 'application/json',
      }

      const response = await $fetch(`/v1/pathway/test_agent/init`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers,
      })

      if (response.errors) {
        throw new Error('Failed to initialize call')
      }

      const client = new BlandWebClient(pathwayId, response.data.id, {
        customEndpoint: config.WS_URL,
      })
      await client.initConversation({
        sampleRate: 44100,
      })

      // Get the most recent call, we have to pause for a certain amount of time so that the call can be populated
      // This is a hacky solution, but it works for now
      await new Promise(resolve => setTimeout(resolve, 2000))
      const call = await $fetch(`/v1/pathway/test_agent/get_call_id`, {
        method: 'POST',
        body: JSON.stringify({
          pathway_id: pathwayId,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const mostRecentCall = call?.data

      setCallID(mostRecentCall?.c_id)
      setShowChatInterface(true)
      setShowChat(true)

      setBlandClient(client)
      setIsCallActive(true)
    }
    catch (error) {
      console.error('Failed to start call:', error)
      toast.error('Failed to start call')
      if (blandClient) {
        blandClient.stopConversation()
      }
    }
    finally {
      setIsLoading(false)
    }
  }

  const handleEndCall = () => {
    if (blandClient) {
      blandClient.stopConversation()
    }
    setIsCallActive(false)
    onClose()
  }

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      {isOpen && (
        <div className={`
          absolute right-0 z-1000 mt-8 max-h-[calc(100vh-100px)] w-[300px]
          origin-top-right overflow-x-clip overflow-y-scroll rounded bg-white
          p-1.5 shadow-lg ring-1 ring-black/5
        `}
        >
          <div className="p-0.5">
            <div
              style={{
                width: '100%',
                justifyContent: 'space-between',
                alignItems: 'center',
                display: 'flex',
                borderBottom: '1px solid #e0e0e0',
                paddingBottom: 8,
              }}
            >
              <p style={{ fontSize: 16, fontWeight: '550' }}>Web Call</p>
              <X
                style={{ height: 16, width: 16, cursor: 'pointer' }}
                onClick={onClose}
              />
            </div>

            {/* Load Configuration Section */}
            <div className="mt-2.5 mb-2.5">
              <label className={`
                block px-1.5 pt-1.5 text-[13px] font-medium text-[#676775]
              `}
              >
                Load Configuration
              </label>
              <Menu as="div" className="relative mt-0.5">
                <Menu.Button className={`
                  flex w-full items-center rounded border border-gray-300 px-2.5
                  py-1.5 text-[13px]
                  focus:border-indigo-500 focus:ring-indigo-500
                  focus:outline-hidden
                `}
                >
                  <span className="grow text-left">
                    {selectedConfig
                      ? selectedConfig.name
                      : 'Select a configuration'}
                  </span>
                  <FaCaretDown className="h-2.5 w-2.5" />
                </Menu.Button>
                <Menu.Items className={`
                  absolute z-10 mt-0.5 max-h-36 w-full overflow-auto rounded
                  bg-white shadow-lg ring-1 ring-black/5
                  focus:outline-hidden
                `}
                >
                  {savedConfigs.map(config => (
                    <Menu.Item key={config.id}>
                      {({ active }) => (
                        <div
                          className={`
                            ${
                        active ? 'bg-indigo-50' : 'bg-white'
                        }
                            ${
                        selectedConfig && selectedConfig.id === config.id
                          ? 'bg-indigo-100'
                          : ''
                        }
                            group flex w-full cursor-pointer items-center
                            justify-between px-2.5 py-1.5 text-xs
                          `}
                          onClick={() => {
                            loadConfig(config)
                            startEditing(config)
                          }}
                        >
                          <span>{config.name}</span>
                          <div className="flex items-center">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                startEditing(config)
                              }}
                              className={`
                                mr-1.5 text-blue-600
                                hover:text-blue-800
                              `}
                            >
                              <FaEdit />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteConfig(config)
                              }}
                              className={`
                                text-red-600
                                hover:text-red-800
                              `}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Menu>
            </div>

            <FormProvider {...methods}>
              <form
                onSubmit={methods.handleSubmit(handleStartCall)}
                className="mt-2.5 flex flex-col gap-y-2.5"
              >
                <Voice className="overflow-visible" fieldName="voice" />
                <Language fieldName="language" />
                <RequestData fieldName="request_data" />
                <BlockInterruptions fieldName="block_interruptions" />
                <Pronunciation fieldName="pronunciation_guide" />

                {/* Save Configuration Section */}
                <div className="mt-2.5">
                  <label className="block text-xs font-medium text-gray-700">
                    {editingConfig ? 'Update' : 'Save'}
                    {' '}
                    Configuration
                  </label>
                  <div className="mt-0.5 flex gap-x-1.5">
                    <input
                      type="text"
                      value={configName}
                      onChange={e => setConfigName(e.target.value)}
                      placeholder="Config Name"
                      className="grow rounded-sm border px-1.5 py-0.5 text-xs"
                    />
                    <button
                      type="button"
                      onClick={saveConfig}
                      className={`
                        rounded-sm bg-indigo-600 px-2 py-0.5 text-xs text-white
                        hover:bg-indigo-700
                      `}
                    >
                      {editingConfig ? 'Update' : 'Save'}
                    </button>
                  </div>
                </div>

                <div className="mt-2.5">
                  {!isCallActive
                    ? (
                        <Button submit loading={isLoading} className="w-full">
                          Start Call
                        </Button>
                      )
                    : (
                        <Button
                          onClick={handleEndCall}
                          appearance="danger"
                          size="sm"
                        >
                          <PhoneOff size={16} />
                        </Button>
                      )}
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      )}
    </div>
  )
}

export default WebClientSlideOut
